/* ==========================================================================
   HyroxFit — shared runtime.

   One self-initialising module per behaviour, each keyed off a data-hx-*
   attribute, so a section only needs the right markup to switch it on.
   Every module re-runs on shopify:section:load, which is what the theme
   editor fires when a merchant edits a section.
   ========================================================================== */

(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---- helpers ------------------------------------------------------- */

  function all(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  /* Guards against double initialisation. The key must never be the camelCase
     form of an attribute that carries a value — writing the flag would clobber
     it (data-hx-sticky holds a CSS selector), hence the *Init suffixes. */
  function once(el, key) {
    if (el.dataset[key] === 'on') return false;
    el.dataset[key] = 'on';
    return true;
  }

  function clamp(n, min, max) {
    return Math.min(max, Math.max(min, n));
  }

  /* Formats cents using the shop's money_format string. Supports the four
     placeholders Shopify emits; falls back to a plain decimal otherwise. */
  function formatMoney(cents, format) {
    var value = (cents || 0) / 100;
    var fmt = format || '${{amount}}';

    function withDelimiters(num, precision, thousands, decimal) {
      var fixed = num.toFixed(precision);
      var parts = fixed.split('.');
      var int = parts[0].replace(/(\d)(?=(\d\d\d)+$)/g, '$1' + thousands);
      return parts[1] ? int + decimal + parts[1] : int;
    }

    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, name) {
      switch (name) {
        case 'amount_no_decimals':
          return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(value, 0, '.', ',');
        default:
          return withDelimiters(value, 2, ',', '.');
      }
    });
  }

  /* Turns a rendered money string back into cents. Kaching prints the offer
     price as text and it is the only place that price exists, so it has to be
     read back rather than recomputed. Handles "$29.99", "29,99 €", "$1,234.56". */
  function parseMoney(text) {
    if (!text) return null;
    var cleaned = String(text).replace(/[^0-9.,]/g, '');
    if (!cleaned) return null;
    var lastSep = Math.max(cleaned.lastIndexOf('.'), cleaned.lastIndexOf(','));
    var units, decimals;
    if (lastSep !== -1 && cleaned.length - lastSep - 1 <= 2) {
      units = cleaned.slice(0, lastSep).replace(/[.,]/g, '');
      decimals = cleaned.slice(lastSep + 1).padEnd(2, '0');
    } else {
      units = cleaned.replace(/[.,]/g, '');
      decimals = '00';
    }
    var cents = parseInt(units || '0', 10) * 100 + parseInt(decimals, 10);
    return isNaN(cents) ? null : cents;
  }

  function emit(target, name, detail) {
    target.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
  }

  /* ==================================================================== */
  /* FAQ accordion — one open at a time                                    */
  /* ==================================================================== */

  function initFaq(root) {
    all('[data-hx-faq]', root).forEach(function (faq) {
      if (!once(faq, 'hxFaqInit')) return;
      var buttons = all('[data-hx-faq-q]', faq);

      buttons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var isOpen = btn.getAttribute('aria-expanded') === 'true';
          buttons.forEach(function (other) {
            var panel = document.getElementById(other.getAttribute('aria-controls'));
            other.setAttribute('aria-expanded', 'false');
            if (panel) panel.hidden = true;
          });
          if (!isOpen) {
            btn.setAttribute('aria-expanded', 'true');
            var own = document.getElementById(btn.getAttribute('aria-controls'));
            if (own) own.hidden = false;
          }
        });
      });
    });
  }

  /* ==================================================================== */
  /* Header — mobile menu                                                  */
  /* ==================================================================== */

  function initHeader(root) {
    all('[data-hx-burger]', root).forEach(function (btn) {
      if (!once(btn, 'hxBurgerInit')) return;

      var nav = document.getElementById(btn.getAttribute('aria-controls'));
      if (!nav) return;

      function setOpen(open) {
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        nav.dataset.open = open ? 'true' : 'false';
      }

      btn.addEventListener('click', function () {
        setOpen(btn.getAttribute('aria-expanded') !== 'true');
      });

      /* following a link, pressing Escape, or clicking away all close it */
      nav.addEventListener('click', function (evt) {
        if (evt.target.closest('a')) setOpen(false);
      });
      document.addEventListener('keydown', function (evt) {
        if (evt.key === 'Escape' && btn.getAttribute('aria-expanded') === 'true') {
          setOpen(false);
          btn.focus();
        }
      });
      document.addEventListener('click', function (evt) {
        if (btn.getAttribute('aria-expanded') !== 'true') return;
        if (!nav.contains(evt.target) && !btn.contains(evt.target)) setOpen(false);
      });

      setOpen(false);
    });
  }

  /* ==================================================================== */
  /* Before / after comparator                                             */
  /* ==================================================================== */

  function initCompare(root) {
    all('[data-hx-compare]', root).forEach(function (box) {
      if (!once(box, 'hxCompareInit')) return;

      var overlay = box.querySelector('[data-hx-compare-overlay]');
      var divider = box.querySelector('[data-hx-compare-divider]');
      if (!overlay || !divider) return;

      var dragging = false;

      function setPosition(pct) {
        var p = clamp(pct, 0, 100);
        overlay.style.width = p + '%';
        divider.style.left = p + '%';
        divider.setAttribute('aria-valuenow', Math.round(p));
        /* keep the clipped image at full comparator width so it does not squash */
        box.style.setProperty('--hx-compare-w', box.clientWidth + 'px');
      }

      function fromEvent(evt) {
        var rect = box.getBoundingClientRect();
        if (!rect.width) return;
        setPosition(((evt.clientX - rect.left) / rect.width) * 100);
      }

      box.addEventListener('pointerdown', function (evt) {
        dragging = true;
        if (box.setPointerCapture) {
          try { box.setPointerCapture(evt.pointerId); } catch (e) { /* older Safari */ }
        }
        fromEvent(evt);
      });
      box.addEventListener('pointermove', function (evt) {
        if (dragging) fromEvent(evt);
      });
      /* pointerleave is deliberately absent: the pointer is captured, so dragging
         past the edge should keep tracking (clamped) instead of stopping dead. */
      ['pointerup', 'pointercancel'].forEach(function (type) {
        box.addEventListener(type, function () { dragging = false; });
      });
      window.addEventListener('pointerup', function () { dragging = false; });

      /* Keyboard: the prototype has none, production must. */
      divider.addEventListener('keydown', function (evt) {
        var current = parseFloat(divider.getAttribute('aria-valuenow')) || 50;
        var step = evt.shiftKey ? 10 : 2;
        if (evt.key === 'ArrowLeft' || evt.key === 'ArrowDown') {
          setPosition(current - step);
        } else if (evt.key === 'ArrowRight' || evt.key === 'ArrowUp') {
          setPosition(current + step);
        } else if (evt.key === 'Home') {
          setPosition(0);
        } else if (evt.key === 'End') {
          setPosition(100);
        } else {
          return;
        }
        evt.preventDefault();
      });

      var start = parseFloat(box.dataset.hxCompareStart);
      setPosition(isNaN(start) ? 50 : start);
      window.addEventListener('resize', function () {
        setPosition(parseFloat(divider.getAttribute('aria-valuenow')) || 50);
      });
    });
  }

  /* ==================================================================== */
  /* Product gallery                                                       */
  /* ==================================================================== */

  function initGallery(root) {
    all('[data-hx-gallery]', root).forEach(function (gallery) {
      if (!once(gallery, 'hxGalleryInit')) return;

      var slides = all('[data-hx-gallery-slide]', gallery);
      var thumbs = all('[data-hx-gallery-thumb]', gallery);
      var counter = gallery.querySelector('[data-hx-gallery-fig]');
      var total = slides.length;

      function show(index) {
        slides.forEach(function (slide, i) { slide.hidden = i !== index; });
        thumbs.forEach(function (thumb, i) {
          thumb.setAttribute('aria-selected', i === index ? 'true' : 'false');
        });
        if (counter) {
          counter.textContent =
            'Fig. ' + String(index + 1).padStart(2, '0') + ' / ' + String(total).padStart(2, '0');
        }
      }

      thumbs.forEach(function (thumb, i) {
        thumb.addEventListener('click', function () { show(i); });
      });

      if (total) show(0);
    });
  }

  /* ==================================================================== */
  /* Pack selector — Kaching bridge                                        */
  /*                                                                       */
  /* Kaching renders its widget at runtime and its markup is not knowable  */
  /* from the theme, so this reads whatever it can and always falls back   */
  /* to the form's own quantity field.                                     */
  /* ==================================================================== */

  function initPack(root) {
    all('[data-hx-pack]', root).forEach(function (pack) {
      if (!once(pack, 'hxPackInit')) return;

      var widget = pack.querySelector('kaching-bundle');
      var fallback = pack.querySelector('[data-hx-pack-fallback]');

      function selectedOfferBar() {
        if (!widget) return null;
        /* scope to the offer list so upsell rows can never be mistaken for it */
        var scope = widget.querySelector('.kaching-bundles__bars') || widget;
        return scope.querySelector('.kaching-bundles__bar--selected') ||
               scope.querySelector('[class*="bar--selected"]');
      }

      function readWidgetQuantity() {
        if (!widget) return null;

        /* The offers are "buy 1 get 1 free" and "buy 2 get 2 free", not a
           quantity ladder: the honest unit count is how many per-item rows the
           selected offer prints (2 and 4), not the radio's own value. */
        var bar = selectedOfferBar();
        if (bar) {
          var rows = bar.querySelectorAll('.kaching-bundles__bar-variant').length;
          if (rows > 0) return rows;
        }

        var checked = widget.querySelector('input[type="radio"]:checked, [aria-checked="true"]');
        if (!checked) return null;
        var attrs = ['data-quantity', 'data-qty', 'data-bundle-quantity', 'value'];
        for (var i = 0; i < attrs.length; i++) {
          var raw = checked.getAttribute(attrs[i]);
          var n = parseInt(raw, 10);
          if (!isNaN(n) && n > 0 && n < 100) return n;
        }
        /* Last resort: a leading integer in the row's text ("2 layers"). */
        var row = checked.closest('label, [class*="bar"], [class*="item"]') || checked;
        var match = (row.textContent || '').match(/(\d+)\s*(?:x|×)?\s*\w/);
        if (match) {
          var m = parseInt(match[1], 10);
          if (m > 0 && m < 100) return m;
        }
        return null;
      }

      /* The offer price is Kaching's, not ours: a "buy 1 get 1 free" deal costs
         $29.99 while the variant unit price is $59.90, so computing
         unit x quantity would advertise the wrong total on the button. */
      function barPriceCents(bar) {
        if (!bar) return null;
        var priceEl = bar.querySelector('.kaching-bundles__bar-price') ||
                      bar.querySelector('[class*="bar-price"]');
        return priceEl ? parseMoney((priceEl.textContent || '').trim()) : null;
      }

      function readWidgetPrice() {
        if (!widget) return null;
        var cents = barPriceCents(selectedOfferBar());
        if (cents === null) return null;

        /* Checkbox upsells are separate rows that do reach the cart, so the
           advertised total has to include every ticked one. Their state lives on
           an inner div[role=checkbox][aria-checked], not on the row and not on a
           real input — and ".kaching-bundles__free-gift" is something else
           entirely (the banner inside the buy-2-get-2 bar), not this. */
        var upsells = widget.querySelectorAll('.kaching-bundles__checkbox-upsells__upsell');
        Array.prototype.forEach.call(upsells, function (row) {
          var box = row.querySelector('[role="checkbox"]');
          if (!box || box.getAttribute('aria-checked') !== 'true') return;
          var extra = barPriceCents(row);
          if (extra !== null) cents += extra;
        });
        return { cents: cents };
      }

      /* Clicking the body of the already-selected offer toggles it off, leaving
         no offer at all: both radios clear, the CTA silently falls back to the
         unit price and the bundle is no longer what gets added. An offer must
         always be selected, so the last one is put back. */
      var lastOfferIndex = 0;
      var restoringOffer = false;

      function enforceOfferSelection() {
        if (!widget || restoringOffer) return false;
        var scope = widget.querySelector('.kaching-bundles__bars');
        if (!scope) return false;
        var bars = scope.querySelectorAll('.kaching-bundles__bar');
        if (!bars.length) return false;

        for (var i = 0; i < bars.length; i++) {
          if (bars[i].classList.contains('kaching-bundles__bar--selected')) {
            lastOfferIndex = i;
            return false;
          }
        }

        var target = bars[lastOfferIndex] || bars[0];
        var hit = target.querySelector('.kaching-bundles__bar-main') || target;
        restoringOffer = true;
        hit.click();
        setTimeout(function () { restoringOffer = false; }, 0);
        return true;
      }

      function publishPack() {
        if (enforceOfferSelection()) return;
        var qty = readWidgetQuantity();
        var live = !!(widget && widget.children.length);
        if (qty === null && fallback && !live) {
          var row = fallback.querySelector('[aria-checked="true"]');
          if (row) qty = parseInt(row.dataset.hxPackQty, 10) || 1;
        }
        var price = live ? readWidgetPrice() : null;

        var optionLabels = null;
        var offerBar = live ? selectedOfferBar() : null;
        if (offerBar) {
          var firstRow = offerBar.querySelector('.kaching-bundles__bar-variant');
          var selects = (firstRow || offerBar).querySelectorAll('.kaching-bundles__bar-variant-select, select');
          if (selects.length) {
            optionLabels = Array.prototype.map.call(selects, function (el) {
              /* option values carry their bracket gloss, e.g.
                 "S (110 - 154 lbs)"; the bar wants the short form */
              return (el.value || el.textContent || '').trim().replace(/\s*\([^)]*\)\s*$/, '');
            });
          }
        }

        emit(pack, 'hyroxfit:pack-change', {
          quantity: qty || 1,
          priceCents: price ? price.cents : null,
          optionLabels: optionLabels,
          source: live ? 'kaching' : 'fallback'
        });
      }

      /* Kaching renders asynchronously. If nothing has appeared inside the
         custom element shortly after load, the app is absent (theme editor,
         disabled, blocked) and the plain ladder takes over.

         The latch matters: Kaching empties and refills its own element when the
         variant changes, so a naive check flips the fallback back on mid-render
         and the shopper ends up looking at two pack ladders. Once the app has
         proved itself alive, the fallback stays down for good. */
      var kachingProvenLive = false;

      function revealFallbackIfNeeded() {
        if (widget && widget.children.length > 0) kachingProvenLive = true;
        if (!fallback) return;
        if (kachingProvenLive) {
          fallback.hidden = true;
          return;
        }
        fallback.hidden = false;
        publishPack();
      }

      if (widget) {
        new MutationObserver(function () {
          /* Kaching may render later than the reveal timeout; if it does, the
             fallback has to step back down so the two ladders never coexist. */
          revealFallbackIfNeeded();
          publishPack();
        }).observe(widget, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['class', 'aria-checked', 'checked']
        });
        widget.addEventListener('change', publishPack);
        widget.addEventListener('click', function () { setTimeout(publishPack, 0); });
      }

      if (fallback) {
        setTimeout(revealFallbackIfNeeded, 1200);
        all('[data-hx-pack-qty]', fallback).forEach(function (row) {
          row.addEventListener('click', function () {
            all('[data-hx-pack-qty]', fallback).forEach(function (other) {
              other.setAttribute('aria-checked', other === row ? 'true' : 'false');
            });
            publishPack();
          });
        });
      }

      publishPack();
    });
  }

  /* ==================================================================== */
  /* Buy area — colour / size / price / CTA state                          */
  /* ==================================================================== */

  function initBuy(root) {
    all('[data-hx-buy]', root).forEach(function (buy) {
      if (!once(buy, 'hxBuyInit')) return;

      /* The variant payload must live inside [data-hx-buy]; fall back to the
         closest section so a stray placement degrades instead of silently
         zeroing every price. */
      var dataEl = buy.querySelector('[data-hx-variants]') ||
                   (buy.closest('section') || document).querySelector('[data-hx-variants]');
      var variants = [];
      if (dataEl) {
        try { variants = JSON.parse(dataEl.textContent) || []; } catch (e) { variants = []; }
      }
      if (!variants.length && window.console) {
        console.warn('[hyroxfit] no variant data found — prices cannot be computed');
      }

      var moneyFormat = buy.dataset.hxMoneyFormat || '${{amount}}';
      var colorPos = parseInt(buy.dataset.hxColorPosition, 10) || 0;   /* 0 = absent */
      var sizePos = parseInt(buy.dataset.hxSizePosition, 10) || 0;

      var idInput = buy.querySelector('input[name="id"]');
      var qtyInput = buy.querySelector('input[name="quantity"]');
      var hint = buy.querySelector('[data-hx-hint]');
      var atc = buy.querySelector('[data-hx-atc]');
      var atcLabel = buy.querySelector('[data-hx-atc-label]');
      var installment = buy.querySelector('[data-hx-installment]');
      var colorOut = all('[data-hx-color-out]', document);

      /* `color`/`size` hold the raw Shopify option values (which can be verbose,
         e.g. "S (110 - 154 lbs)"); the *Label pair holds what the design shows. */
      /* When the bundle owns the variant these groups are not rendered at all;
         readiness then has to come from the form's own variant, otherwise the
         CTA waits forever for a selection the shopper can no longer make. */
      var hasColorPicker = !!buy.querySelector('[data-hx-option="color"]');
      var hasSizePicker = !!buy.querySelector('[data-hx-option="size"]');

      var state = {
        color: null,
        size: null,
        colorLabel: null,
        sizeLabel: null,
        quantity: 1,
        packSource: 'fallback',
        packPriceCents: null
      };

      function optionOf(variant, position) {
        return position ? variant['option' + position] : null;
      }

      function matchingVariant() {
        if (!hasColorPicker && !hasSizePicker) {
          /* the bundle drives the variant; follow whatever the form carries */
          var id = idInput ? parseInt(idInput.value, 10) : NaN;
          var byId = variants.filter(function (v) { return v.id === id; })[0];
          if (byId) return byId;
          return variants.filter(function (v) { return v.available; })[0] || variants[0] || null;
        }
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          if (hasColorPicker && state.color !== null && optionOf(v, colorPos) !== state.color) continue;
          if (hasSizePicker && state.size !== null && optionOf(v, sizePos) !== state.size) continue;
          if (hasColorPicker && state.color === null) continue;
          if (hasSizePicker && state.size === null) continue;
          return v;
        }
        return null;
      }

      /* A colour is sellable if any in-stock variant carries it. */
      function valueAvailable(position, value, otherPosition, otherValue) {
        return variants.some(function (v) {
          if (optionOf(v, position) !== value) return false;
          if (otherPosition && otherValue !== null && optionOf(v, otherPosition) !== otherValue) return false;
          return v.available;
        });
      }

      function unitPrice() {
        var v = matchingVariant();
        if (v) return v.price;
        var first = variants.find(function (x) { return x.available; }) || variants[0];
        return first ? first.price : 0;
      }

      function refresh() {
        var variant = matchingVariant();

        /* hidden id field drives what actually goes in the cart */
        if (idInput && variant) idInput.value = variant.id;

        /* When Kaching is live it owns the quantity and writes its own field;
           writing ours too would put two [name=quantity] inputs in the form and
           the last one would silently win. Only drive it for the fallback. */
        if (qtyInput) {
          if (state.packSource === 'kaching') qtyInput.disabled = true;
          else {
            qtyInput.disabled = false;
            qtyInput.value = state.quantity;
          }
        }

        /* strike through unsellable combinations */
        all('[data-hx-option="size"] [data-value]', buy).forEach(function (btn) {
          var ok = valueAvailable(sizePos, btn.dataset.value, colorPos, state.color);
          btn.disabled = !ok;
        });
        all('[data-hx-option="color"] [data-value]', buy).forEach(function (btn) {
          var ok = valueAvailable(colorPos, btn.dataset.value, sizePos, state.size);
          btn.disabled = !ok;
        });

        /* size hint */
        if (hint) {
          if (state.size === null) {
            hint.textContent = hint.dataset.hxHintEmpty || '→ Select a size to continue';
            hint.dataset.state = 'empty';
          } else {
            var tpl = hint.dataset.hxHintChosen || 'Size [size] selected';
            hint.textContent = tpl.replace('[size]', state.sizeLabel || state.size);
            hint.dataset.state = 'chosen';
          }
        }

        /* CTA — Kaching's advertised offer price wins when the app is driving. */
        var total = state.packPriceCents !== null && state.packPriceCents !== undefined
          ? state.packPriceCents
          : unitPrice() * state.quantity;
        var ready = (!hasSizePicker || state.size !== null) &&
                    (!hasColorPicker || state.color !== null) &&
                    !!variant && variant.available;

        if (atc) {
          atc.disabled = !ready;
          atc.setAttribute('aria-disabled', ready ? 'false' : 'true');
        }
        if (atcLabel && atcLabel.dataset.hxBusy !== 'true') {
          if (!ready) {
            atcLabel.textContent = atc ? (atc.dataset.hxLabelIdle || 'Select a size first') : '';
          } else {
            var tpl2 = atc.dataset.hxLabelReady || 'Add to cart — [price]';
            atcLabel.textContent = tpl2.replace('[price]', formatMoney(total, moneyFormat));
          }
        }
        if (installment) {
          installment.textContent = (installment.dataset.hxTemplate || '4 × [price] INTEREST-FREE')
            .replace('[price]', formatMoney(Math.round(total / 4), moneyFormat));
        }

        /* fallback pack rows carry no pricing of their own — fill them from
           the real variant price so nothing is invented in the theme */
        all('[data-hx-pack-qty]', document).forEach(function (row) {
          var out = row.querySelector('[data-hx-pack-price]');
          if (!out) return;
          var n = parseInt(row.dataset.hxPackQty, 10) || 1;
          out.textContent = formatMoney(unitPrice() * n, moneyFormat);
        });

        /* price row — every figure tracks the selected pack */
        var rowTotal = buy.querySelector('[data-hx-price-total]');
        if (rowTotal) {
          rowTotal.textContent = formatMoney(total, moneyFormat);

          var compareUnit = variant && variant.compare_at_price > variant.price
            ? variant.compare_at_price : 0;
          var compareTotal = compareUnit * state.quantity;
          var was = buy.querySelector('[data-hx-price-was]');
          var chip = buy.querySelector('[data-hx-price-chip]');

          if (was) {
            was.hidden = !compareTotal;
            if (compareTotal) was.textContent = formatMoney(compareTotal, moneyFormat);
          }
          if (chip) {
            /* the discount is computed, never typed in */
            var pct = compareTotal ? Math.round((1 - total / compareTotal) * 100) : 0;
            chip.hidden = pct <= 0;
            if (pct > 0) chip.textContent = '\u2212' + pct + '\u00a0%';
          }
          var units = buy.querySelector('[data-hx-price-units]');
          if (units) {
            units.textContent = state.quantity + (state.quantity > 1 ? ' UNITS' : ' UNIT');
          }
        }

        /* gallery caption + sticky bar */
        colorOut.forEach(function (el) { el.textContent = state.colorLabel || state.color || ''; });

        emit(buy, 'hyroxfit:selection-change', {
          color: state.colorLabel || state.color,
          size: state.sizeLabel || state.size,
          quantity: state.quantity,
          total: total,
          totalFormatted: formatMoney(total, moneyFormat),
          ready: !!ready,
          variant: variant
        });
      }

      all('[data-hx-option] [data-value]', buy).forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.disabled) return;
          var group = btn.closest('[data-hx-option]');
          var key = group.dataset.hxOption;
          state[key] = btn.dataset.value;
          state[key + 'Label'] = btn.dataset.label || btn.dataset.value;
          all('[data-value]', group).forEach(function (other) {
            other.setAttribute('aria-pressed', other === btn ? 'true' : 'false');
          });
          refresh();
        });
      });

      document.addEventListener('hyroxfit:pack-change', function (evt) {
        state.quantity = evt.detail.quantity || 1;
        state.packSource = evt.detail.source || 'fallback';
        state.packPriceCents = evt.detail.priceCents;
        /* Without theme pickers, the bundle's first item is what the sticky bar
           and the gallery caption should name. */
        if (!hasColorPicker && !hasSizePicker && evt.detail.optionLabels) {
          state.colorLabel = evt.detail.optionLabels[0] || null;
          state.sizeLabel = evt.detail.optionLabels[1] || null;
        }
        refresh();
      });

      /* "Added to cart" confirmation, driven by the theme's own cart event. */
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function (payload) {
          if (!payload || payload.source !== 'product-form') return;
          if (!atcLabel) return;
          var done = atc.dataset.hxLabelDone || 'Added to cart ✓';
          atcLabel.dataset.hxBusy = 'true';
          atcLabel.textContent = done;
          setTimeout(function () {
            atcLabel.dataset.hxBusy = 'false';
            refresh();
          }, 1800);
        });
      }

      refresh();
    });
  }

  /* ==================================================================== */
  /* Sticky buy bar                                                        */
  /* ==================================================================== */

  function initSticky(root) {
    all('[data-hx-sticky]', root).forEach(function (bar) {
      if (!once(bar, 'hxStickyInit')) return;

      var target = document.querySelector(bar.dataset.hxSticky);
      if (!target) return;

      var refOut = bar.querySelector('[data-hx-sticky-ref]');
      var subOut = bar.querySelector('[data-hx-sticky-sub]');

      /* Slides in once the buy block has left the viewport — an observer on
         the block itself, not an arbitrary scroll threshold. */
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            bar.dataset.visible = entry.isIntersecting ? 'false' : 'true';
          });
        }, { rootMargin: '0px 0px -60% 0px' }).observe(target);
      } else {
        bar.dataset.visible = 'true';
      }

      document.addEventListener('hyroxfit:selection-change', function (evt) {
        var d = evt.detail;
        if (refOut) {
          refOut.textContent = [refOut.dataset.hxRef, d.color, d.size]
            .filter(Boolean)
            .join(' / ');
        }
        if (subOut) {
          var unit = ' / ' + d.quantity + (d.quantity > 1 ? ' units' : ' unit');
          subOut.textContent = d.totalFormatted + unit;
        }
      });

      /* The bar's button submits the real form rather than duplicating it. */
      var proxy = bar.querySelector('[data-hx-sticky-submit]');
      if (proxy) {
        proxy.addEventListener('click', function () {
          var realBtn = document.querySelector('[data-hx-atc]');
          if (realBtn && !realBtn.disabled) realBtn.click();
          else if (target.scrollIntoView) target.scrollIntoView({ behavior: REDUCED.matches ? 'auto' : 'smooth' });
        });
      }
    });
  }

  /* ==================================================================== */

  function initAll(root) {
    initHeader(root);
    initFaq(root);
    initCompare(root);
    initGallery(root);
    initPack(root);
    initBuy(root);
    initSticky(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* Theme editor: re-init whatever section was just re-rendered. */
  document.addEventListener('shopify:section:load', function (evt) {
    initAll(evt.target);
  });
})();
